import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Zap, ChevronDown } from 'lucide-react';
import './Hero.css';

const TERMINAL_LINES = [
  { prefix: '▶', text: 'Initializing cloud infrastructure deploy...' },
  { prefix: '▶', text: 'Running security scan — 0 vulnerabilities found' },
  { prefix: '▶', text: 'AI model training pipeline: epoch 48/50 ✓' },
  { prefix: '▶', text: 'Kubernetes cluster health: 12/12 nodes ready' },
  { prefix: '▶', text: 'Uptime check: 99.97% over last 90 days' },
  { prefix: '▶', text: 'SSL certificates renewed — expires in 364 days' },
];

const TECH_BADGES = ['AWS', 'Azure', 'Docker', 'Kubernetes', 'React', 'Node.js'];

const STATS = [
  { number: '50+', label: 'Enterprise Clients' },
  { number: '99.9%', label: 'Uptime SLA' },
  { number: '24/7', label: 'Expert Support' },
];

const fadeUp = (delay = 0, duration = 0.7) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const [visibleLines, setVisibleLines] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleLines((prev) => {
        if (prev.length < TERMINAL_LINES.length) {
          return [...prev, TERMINAL_LINES[prev.length]];
        }
        clearInterval(interval);
        return prev;
      });
    }, 600);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="hero" ref={heroRef}>
      {/* ── Animated Background ── */}
      <motion.div className="hero-bg" style={{ y: bgY }}>
        <div className="hero-grid" />
        <div className="hero-orb orb-1" />
        <div className="hero-orb orb-2" />
        <div className="hero-orb orb-3" />
      </motion.div>

      {/* ── Main Content ── */}
      <motion.div className="hero-content container" style={{ opacity: contentOpacity }}>

        {/* ── LEFT — Hero Text ── */}
        <div className="hero-text">
          {/* Eyebrow badge */}
          <motion.div className="hero-eyebrow" {...fadeUp(0.1, 0.6)}>
            <Zap size={14} strokeWidth={2.5} />
            AI-Powered IT Solutions
          </motion.div>

          {/* Headline */}
          <h1 className="hero-title">
            <motion.span
              className="line-1"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              Enterprise IT Services &amp;
            </motion.span>
            <motion.span
              className="gradient-text"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              Custom Software Development
            </motion.span>
          </h1>

          {/* Description */}
          <motion.p className="hero-description" {...fadeUp(0.5, 0.7)}>
            We engineer scalable software, resilient cloud infrastructures, and intelligent AI solutions for forward-thinking enterprises. Trusted by 50+ companies across India for secure digital transformation.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div className="hero-actions" {...fadeUp(0.65, 0.7)}>
            <a href="https://wa.me/918287999236?text=Hi!%20I%20would%20like%20to%20schedule%20a%20free%20IT%20Assessment." target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Get Free IT Assessment
              <ArrowRight size={16} strokeWidth={2.5} />
            </a>
            <a href="https://wa.me/918287999236?text=Hi!%20I%20would%20like%20to%20explore%20your%20Enterprise%20Solutions." target="_blank" rel="noopener noreferrer" className="btn btn-outline">
              See Our Enterprise Solutions
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8, ease: 'easeOut' }}
          >
            {STATS.map((stat, i) => (
              <div className="stat-item" key={i}>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ── RIGHT — Hero Visual ── */}
        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Terminal Card */}
          <div className="terminal-card">
            {/* Terminal header */}
            <div className="terminal-header">
              <span className="terminal-dot dot-red" />
              <span className="terminal-dot dot-yellow" />
              <span className="terminal-dot dot-green" />
              <span className="terminal-title">wayug-systems ~ live</span>
            </div>

            {/* Terminal body */}
            <div className="terminal-body">
              {visibleLines.filter(Boolean).map((line, i) => (
                <motion.div
                  key={i}
                  className="terminal-line"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                >
                  <span className="terminal-prefix">{line?.prefix}</span>
                  <span className="terminal-text">{line?.text}</span>
                </motion.div>
              ))}
              {visibleLines.length < TERMINAL_LINES.length && (
                <div className="terminal-line">
                  <span className="terminal-prefix">▶</span>
                  <span className="terminal-cursor" />
                </div>
              )}
            </div>

            {/* Status bar */}
            <div className="terminal-status-bar">
              <span className="status-dot" />
              All Systems Operational
            </div>
          </div>

          {/* Tech Stack Badges */}
          <div className="tech-stack-badges">
            {TECH_BADGES.map((badge) => (
              <span className="tech-badge" key={badge}>
                {badge}
              </span>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* ── Scroll Indicator ── */}
      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
      >
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
}